import { useState } from "react";
import { billService } from "../services/auditService";
import { stockService } from "../services/apiService";

const isAuthError = (err) => {
  const status = err?.response?.status;
  return status === 401 || status === 403;
};

export const useBill = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bill, setBill] = useState(null);
  const [bills, setBills] = useState([]);

  const generateBillByOrderId = async (orderId) => {
    setLoading(true); setError(null);
    try {
      const data = await billService.generateBillByOrderId(orderId);
      setBill(data); return data;
    } catch (err) {
      if (!isAuthError(err)) { setError(err.message); throw err; }
    } finally { setLoading(false); }
  };

  const generateBillByDate = async (date) => {
    setLoading(true); setError(null);
    try {
      const data = await billService.generateBillByDate(date);
      setBills(data); return data;
    } catch (err) {
      if (!isAuthError(err)) { setError(err.message); throw err; }
    } finally { setLoading(false); }
  };

  const printBill = async (orderId, format = "a4") => {
    setLoading(true); setError(null);
    try {
      if (format === "a4") {
        const blob = await billService.downloadBillPDF(orderId);
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => window.URL.revokeObjectURL(url), 10000);
      } else {
        const bill = await billService.generateBillByOrderId(orderId);
        let showSize = false;
        try {
          const fieldConfigData = await stockService.getClientFieldConfig();
          console.log("[useBill] fieldConfigData:", JSON.stringify(fieldConfigData));
          const sizeConfig = (fieldConfigData || []).find((f) => f.fieldName === "size");
          console.log("[useBill] sizeConfig:", JSON.stringify(sizeConfig));
          showSize = sizeConfig ? !!sizeConfig.visible : false;
        } catch (e) { console.error("[useBill] fieldConfig error:", e); showSize = false; }
        const companyName = localStorage.getItem("companyName") || "Store";
        const date = bill.createdOn
          ? new Date(bill.createdOn).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
          : new Date().toLocaleString("en-IN");
        const gstAmt   = Number(bill.gstAmount  || 0);
        const finalAmt = Number(bill.finalAmount || 0);
        const totalAmt = Number(bill.totalAmount || 0);
        const paidAmt  = Number(bill.paidAmount  || 0);
        const cgst     = (gstAmt / 2).toFixed(2);
        const sgst     = (gstAmt / 2).toFixed(2);
        const taxable  = (finalAmt - gstAmt).toFixed(2);
        const balance  = (finalAmt - paidAmt).toFixed(2);
        const colCount = showSize ? 7 : 6;
        const itemRows = (bill.items || []).map((item, i) => {
          const batchRow = item.batchNo
            ? `<tr><td colspan="${colCount}" style="font-size:7px;color:#555;padding-left:6px;padding-bottom:2px;">Batch: ${item.batchNo}</td></tr>`
            : "";
          const sizeCell = showSize
            ? `<td class="r">${item.size != null && Number(item.size) !== 0 ? item.size : "-"}</td>`
            : "";
          return `<tr><td>${i + 1}</td><td>${item.productName || "-"}</td><td class="r">${item.quantity}</td>${sizeCell}<td class="r">${Number(item.mrp || 0).toFixed(2)}</td><td class="r">${Number(item.discount || 0).toFixed(2)}%</td><td class="r">${Number(item.sellingPrice || 0).toFixed(2)}</td></tr>${batchRow}`;
        }).join("");

        const html = `<!DOCTYPE html><html><head><title>Invoice ${bill.billNo || orderId}</title>
          <style>
            * { margin:0; padding:0; box-sizing:border-box; }
            body { font-family:monospace; width:80mm; margin:0 auto; padding:2mm; font-size:9px; color:#000; }
            .c { text-align:center; } .r { text-align:right; }
            .hdr { text-align:center; border-bottom:1px dashed #000; padding-bottom:2mm; margin-bottom:2mm; }
            .hdr h2 { font-size:12px; font-weight:bold; }
            .hdr p { font-size:8px; }
            .sec { margin-bottom:2mm; }
            .sec div { display:flex; justify-content:space-between; padding:0.5px 0; }
            .dash { border-top:1px dashed #000; margin:2mm 0; }
            .line { border-top:1px solid #000; margin:2mm 0; }
            table { width:100%; border-collapse:collapse; margin-bottom:2mm; }
            th, td { padding:1px 1px; font-size:8px; text-align:left; }
            th { border-bottom:1px solid #000; font-weight:bold; }
            .tot { margin-bottom:2mm; }
            .tot div { display:flex; justify-content:space-between; padding:0.5px 0; font-size:9px; }
            .grand { font-weight:bold; font-size:10px; border-top:1px solid #000; padding-top:1mm; margin-top:1mm; }
            .terms { font-size:7px; color:#555; margin-bottom:2mm; }
            .footer { text-align:center; border-top:1px dashed #000; padding-top:2mm; font-size:8px; }
            .client-bottom { text-align:center; border-top:1px solid #000; padding-top:2mm; margin-top:2mm; font-size:8px; }
            @page { margin:0; size:80mm auto; }
            @media print { @page { margin:0; size:80mm auto; } body { width:80mm; } }
          </style></head><body>

          <div class="hdr">
            <h2>${companyName}</h2>
            ${bill.gstin ? `<p>GSTIN: ${bill.gstin}</p>` : ""}
            <p>TAX INVOICE</p>
            <p>${bill.billNo || "-"} | ${date}</p>
          </div>

          <div class="sec">
            <div><span>Order ID:</span><span>${bill.orderId || "-"}</span></div>
            <div><span>Customer:</span><span>${bill.customerName || "-"}</span></div>
            <div><span>Mobile:</span><span>${bill.customerMobile || "-"}</span></div>
            ${bill.customerAddress ? `<div><span>Address:</span><span>${bill.customerAddress}</span></div>` : ""}
            <div><span>Payment:</span><span>${bill.paymentMode || "-"} | ${bill.transactionId || "-"}</span></div>
          </div>

          <table>
            <thead><tr><th>#</th><th>Product</th><th class="r">Qty</th>${showSize ? '<th class="r">Size</th>' : ""}<th class="r">MRP</th><th class="r">Disc</th><th class="r">Total</th></tr></thead>
            <tbody>${itemRows}</tbody>
          </table>

          <div class="tot">
            <div><span>Subtotal:</span><span>${totalAmt.toFixed(2)}</span></div>
            <div><span>Taxable Amt:</span><span>${taxable}</span></div>
            <div><span>CGST:</span><span>${cgst}</span></div>
            <div><span>SGST:</span><span>${sgst}</span></div>
            <div><span>Total GST:</span><span>${gstAmt.toFixed(2)}</span></div>
            <div class="grand"><span>Grand Total:</span><span>&#8377;${finalAmt.toFixed(2)}</span></div>
            <div><span>Paid Amount:</span><span>${paidAmt.toFixed(2)}</span></div>
            <div><span>Rest Amount:</span><span>${balance}</span></div>
          </div>

          <div class="terms">
            Terms &amp; Conditions:<br/>
            1. Goods once sold will not be taken back.<br/>
            2. No claim will be entertained after delivery.<br/>
            3. Subject to local jurisdiction.<br/>
            4. E&amp;OE - Errors and Omissions Excepted.
          </div>

          <div class="footer">This is a computer-generated invoice. No signature required.</div>

          ${(bill.clientAddress || bill.clientPhone || bill.clientEmail) ? `
          <div class="client-bottom">
            ${bill.clientAddress ? `<p>${bill.clientAddress}</p>` : ""}
            ${(bill.clientPhone || bill.clientEmail) ? `<p>${bill.clientPhone ? "Ph: " + bill.clientPhone : ""}${bill.clientPhone && bill.clientEmail ? " | " : ""}${bill.clientEmail || ""}</p>` : ""}
          </div>` : ""}

          </body></html>`;

        const pw = window.open("", "_blank");
        pw.document.write(html);
        pw.document.close();
        setTimeout(() => { pw.print(); setTimeout(() => pw.close(), 1500); }, 400);
      }
    } catch (err) {
      if (!isAuthError(err)) { setError(err.message); throw err; }
    } finally { setLoading(false); }
  };

  const previewBillPDF = async (orderId) => {
    setLoading(true); setError(null);
    try {
      const blob = await billService.downloadBillPDF(orderId);
      const url = window.URL.createObjectURL(blob);
      return url; // caller is responsible for revoking
    } catch (err) {
      if (!isAuthError(err)) { setError(err.message); throw err; }
    } finally { setLoading(false); }
  };

  const downloadBillPDF = async (orderId) => {
    setLoading(true); setError(null);
    try {
      const blob = await billService.downloadBillPDF(orderId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `bill_${orderId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (!isAuthError(err)) { setError(err.message); throw err; }
    } finally { setLoading(false); }
  };

  const downloadBillExcel = async (orderId) => {
    setLoading(true); setError(null);
    try {
      const blob = await billService.downloadBillExcel(orderId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `bill_${orderId}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (!isAuthError(err)) { setError(err.message); throw err; }
    } finally { setLoading(false); }
  };

  return { loading, error, bill, bills, generateBillByOrderId, generateBillByDate, printBill, previewBillPDF, downloadBillPDF, downloadBillExcel };
};
